import { useState } from "react";
import type { JSX, ReactNode } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { usePageTitle } from "@/components/common/AppShell";
import { useDeviceProfile } from "../hooks/useDeviceProfile";
import { CategoriesSegmentsPanel } from "../profile/CategoriesSegmentsPanel";
import {
  ClassificationPanel,
  hasClassificationContent,
} from "../profile/ClassificationPanel";
import { EngagementPanel } from "../profile/EngagementPanel";
import { formatDate, formatNumber } from "../profile/format";
import { GeoPanel } from "../profile/GeoPanel";
import { OverviewPanel } from "../profile/OverviewPanel";
import { PlatformPanel } from "../profile/PlatformPanel";
import { ValueWithRangePanel } from "../profile/ValueWithRangePanel";
import { VastsPanel } from "../profile/VastsPanel";

type SectionProps = {
  title: string;
  children: ReactNode;
};

const Section = ({ title, children }: SectionProps): JSX.Element => (
  <section className="mb-8">
    <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-(--text-secondary)">
      {title}
    </h3>
    {children}
  </section>
);

const DeviceDetailPage = (): JSX.Element => {
  const { deviceId: deviceIdParam } = useParams<{ deviceId: string }>();
  const deviceId = deviceIdParam ?? null;
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.replace(/\/device\/.*$/, "");

  const handleBack = (): void => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(`${basePath}/devices`);
    }
  };

  const [historyPage, setHistoryPage] = useState(1);
  const historyPageSize = 25;

  usePageTitle(deviceId ? `Device ${deviceId}` : "Device");

  const { profile, isLoading, errorMessage } = useDeviceProfile({
    deviceId,
    historyPage,
    historyPageSize,
  });

  return (
    <section className="flex min-w-0 flex-col">
      <div className="mb-4 flex items-center gap-3">
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center gap-2 rounded-(--radius-md) border border-(--border-strong) bg-(--surface-1) px-3 py-1.5 text-[12px] font-semibold text-(--text-primary) transition-colors hover:border-(--brand-primary)"
        >
          <ArrowLeft size={14} />
          Back to devices
        </button>
      </div>

      <div className="mb-6 space-y-1">
        <h2 className="text-xl font-semibold">
          Device {profile?.deviceId ?? deviceId}
        </h2>
        <p className="text-xs text-(--text-secondary)">
          First seen {formatDate(profile?.range?.firstSeen)} · Last seen{" "}
          {formatDate(profile?.range?.lastSeen)} ·{" "}
          {formatNumber(profile?.vastCount ?? 0)} VASTs
        </p>
      </div>

      {errorMessage ? (
        <div className="mb-3 rounded-lg border border-rose-500/25 bg-rose-500/10 px-3 py-2 text-sm text-rose-100">
          {errorMessage}
        </div>
      ) : null}

      {isLoading && !profile ? (
        <p className="text-sm text-(--text-secondary)">Loading profile…</p>
      ) : profile ? (
        <>
          <Section title="Overview">
            <OverviewPanel profile={profile} />
            {hasClassificationContent(profile) ? (
              <div className="mt-6">
                <ClassificationPanel profile={profile} />
              </div>
            ) : null}
          </Section>

          {profile.ips.length > 0 ? (
            <Section title="IPs">
              <ValueWithRangePanel rows={profile.ips} valueLabel="IP" />
            </Section>
          ) : null}

          {profile.userAgents.length > 0 ? (
            <Section title="User agents">
              <ValueWithRangePanel
                rows={profile.userAgents}
                valueLabel="User agent"
              />
            </Section>
          ) : null}

          {profile.geo.length > 0 ? (
            <Section title="Geo">
              <GeoPanel profile={profile} />
            </Section>
          ) : null}

          {profile.platforms.length > 0 ? (
            <Section title="Platform">
              <PlatformPanel profile={profile} />
            </Section>
          ) : null}

          {profile.categoriesServed.length > 0 ||
          profile.segments.length > 0 ? (
            <Section title="Categories & Segments">
              <CategoriesSegmentsPanel profile={profile} />
            </Section>
          ) : null}

          {profile.engagement.totalVasts > 0 ? (
            <Section title="Engagement">
              <EngagementPanel profile={profile} />
            </Section>
          ) : null}

          {profile.vastHistory.total > 0 ? (
            <Section title="Raw VASTs">
              <VastsPanel
                rows={profile.vastHistory.rows}
                page={profile.vastHistory.page}
                pageSize={profile.vastHistory.pageSize}
                total={profile.vastHistory.total}
                onPageChange={setHistoryPage}
              />
            </Section>
          ) : null}
        </>
      ) : (
        <p className="text-sm text-(--text-secondary)">No data.</p>
      )}
    </section>
  );
};

export default DeviceDetailPage;
